import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Box,
  HStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../api';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
} from 'firebase/firestore';
import { getOrderSummary } from '../Order';

const PAGE_SIZE = 3;

function docToOrder(doc) {
  const orderData = doc.data();
  const orderArray = orderData.order || [];
  let total = 0;
  orderArray.forEach((item) => {
    total +=
      (item.menuPrice || 0) * (item.quantity || 0) +
      (item.productPrice || 0) * (item.quantity || 0);
  });
  return {
    id: doc.id,
    orderId: doc.id,
    createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt,
    status: orderData.status || 'unknown',
    selectedDate: orderData.selectedDate?.toDate?.() || orderData.selectedDate,
    paymentType: orderData.paymentType || 'unknown',
    total,
    orderItems: orderArray,
  };
}

function ModalOrderHistory({ isOpen, onClose }) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [pagesCache, setPagesCache] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [lastDocSnapshot, setLastDocSnapshot] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !user) return;

    const fetchFirstPage = async () => {
      setIsLoading(true);
      setError('');
      setPagesCache({});
      setCurrentPage(0);
      setLastDocSnapshot(null);
      setHasNextPage(false);

      try {
        const ordersQuery = query(
          collection(db, 'order'),
          where('cashierEmail', '==', user.email),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );

        const querySnapshot = await getDocs(ordersQuery);
        const orders = [];
        querySnapshot.forEach((doc) => {
          orders.push(docToOrder(doc));
        });

        const lastDoc =
          querySnapshot.docs.length > 0
            ? querySnapshot.docs[querySnapshot.docs.length - 1]
            : null;

        setPagesCache({ 0: orders });
        setLastDocSnapshot(lastDoc);
        setHasNextPage(querySnapshot.docs.length === PAGE_SIZE);
      } catch (err) {
        console.error('Errore nel fetch ordini:', err);
        setError('Errore nel caricamento degli ordini');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFirstPage();
  }, [isOpen, user]);

  const fetchNextPage = async () => {
    if (!user || !lastDocSnapshot) return;

    const nextPageIndex = currentPage + 1;
    if (pagesCache[nextPageIndex]) {
      setCurrentPage(nextPageIndex);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const ordersQuery = query(
        collection(db, 'order'),
        where('cashierEmail', '==', user.email),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocSnapshot),
        limit(PAGE_SIZE)
      );

      const querySnapshot = await getDocs(ordersQuery);
      const orders = [];
      querySnapshot.forEach((doc) => {
        orders.push(docToOrder(doc));
      });

      const newLastDoc =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      setPagesCache((prev) => ({ ...prev, [nextPageIndex]: orders }));
      setLastDocSnapshot(newLastDoc);
      setHasNextPage(querySnapshot.docs.length === PAGE_SIZE);
      setCurrentPage(nextPageIndex);
    } catch (err) {
      console.error('Errore nel fetch ordini:', err);
      setError('Errore nel caricamento degli ordini');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleString('it', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const currentOrders = pagesCache[currentPage] || [];
  const hasPrev = currentPage > 0;
  const hasCachedNext = Boolean(pagesCache[currentPage + 1]);
  const canGoNext = hasCachedNext || hasNextPage;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      blockScrollOnMount
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalCloseButton />
          <VStack align="start" spacing={1}>
            <Text>Storico Ordini</Text>
            {user?.email && (
              <Text fontSize="sm" fontWeight="normal">
                {user.email}
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {error && (
              <Text fontSize="lg" color="tomato">
                {error}
              </Text>
            )}
            {isLoading && <Text>Caricamento...</Text>}
            {!isLoading && currentOrders.length === 0 && !error && (
              <Text>Nessun ordine trovato.</Text>
            )}
            {!isLoading &&
              currentOrders.map((order) => {
                const summary = getOrderSummary(order.orderItems);
                return (
                <Box
                  key={order.id}
                  p={4}
                  border="1px solid"
                  borderColor="teal"
                  borderRadius="md"
                >
                  <VStack align="stretch" spacing={1}>
                    <HStack justify="space-between" wrap="wrap" gap={2}>
                      <Text fontWeight={600} color="gold">{order.orderId}</Text>
                      <Button
                        size="md"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/checkout-ok/${order.orderId}`);
                          onClose();
                        }}
                      >
                        QR Code
                      </Button>
                    </HStack>
                    <Text fontSize="sm" color={summary.color}>
                      {summary.text}
                    </Text>
                    <Text fontSize="sm">Creato: {formatDate(order.createdAt)}</Text>
                    <Text fontSize="sm">Status: {order.status}</Text>
                    <Text fontSize="sm">
                      Data selezionata: {formatDate(order.selectedDate)}
                    </Text>
                    <Text fontSize="sm">Pagamento: {order.paymentType}</Text>
                    <Text fontSize="sm" fontWeight={600}>
                      Totale: € {order.total.toFixed(2)}
                    </Text>
                  </VStack>
                </Box>
                );
              })}
            {!isLoading && (currentOrders.length > 0 || hasPrev) && (
              <HStack mt={4} justify="space-between" w="100%">
                <Button
                  size="sm"
                  isDisabled={!hasPrev}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Precedente
                </Button>
                <Text fontSize="sm">Pagina {currentPage + 1}</Text>
                <Button
                  size="sm"
                  isDisabled={!canGoNext}
                  isLoading={isLoading}
                  onClick={fetchNextPage}
                >
                  Successiva
                </Button>
              </HStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button minWidth={125} size="lg" onClick={onClose}>
            Chiudi
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ModalOrderHistory;
